<script setup lang="ts">
/**
 * BG §22.4 — the command button. Electric Cyan ground, black text (11.0:1), square corners, a cyan glow;
 * a dirty-white edge and a small horizontal glitch shift on hover; a brief compression on press. Never
 * magenta: magenta is kept for issues (BG §18.3). The portal's primary Button is the same command.
 *
 * `loading` swaps the label for `loadingLabel` (`REQUESTING...`) and marks the button `aria-busy`,
 * so the state is announced rather than only seen — BG §22.3 forbids leaning on visuals alone.
 */
import { computed } from 'vue'

const props = withDefaults(
    defineProps<{
        type?: 'button' | 'submit' | 'reset'
        disabled?: boolean
        loading?: boolean
        /** Replaces the slot while `loading`. Uppercase, in the operational voice. */
        loadingLabel?: string
        /** `command` is the cyan CTA; `ghost` is an outlined secondary for non-primary actions. */
        variant?: 'command' | 'ghost'
    }>(),
    {
        type: 'button',
        disabled: false,
        loading: false,
        loadingLabel: 'REQUESTING...',
        variant: 'command',
    },
)

/** A loading button must not submit twice; disabling it is the guard, not just the styling. */
const inert = computed(() => props.disabled || props.loading)
</script>

<template>
    <button
        class="zh-button"
        :type="type"
        :data-variant="variant"
        :disabled="inert"
        :aria-busy="loading ? 'true' : undefined"
    >
        <span v-if="loading" class="zh-button__label">{{ loadingLabel }}</span>
        <span v-else class="zh-button__label"><slot /></span>
        <span v-if="loading" class="zh-button__cursor" aria-hidden="true">_</span>
    </button>
</template>

<style scoped>
.zh-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.25rem;
    padding: 0 1.75rem;
    min-height: 3.25rem;
    border: 1px solid var(--electric-cyan);
    border-radius: 0;
    background: var(--electric-cyan);
    color: var(--void-black);
    font-family: var(--font-mono);
    font-size: 0.8125rem;
    font-weight: 700;
    letter-spacing: var(--tracking-system);
    text-transform: uppercase;
    white-space: nowrap;
    cursor: pointer;
    box-shadow: var(--glow-cyan);
    transition:
        box-shadow var(--motion-ui) ease-out,
        transform var(--motion-ui) ease-out,
        filter var(--motion-ui) ease-out;
}

.zh-button[data-variant='ghost'] {
    background: transparent;
    color: var(--dirty-white);
    border-color: var(--input);
    box-shadow: none;
}

.zh-button:hover:not(:disabled) {
    filter: brightness(1.12);
    /* BG §22.4 — a dirty-white edge, and a 1px shift that reads as a glitch rather than a lift. */
    box-shadow:
        var(--glow-cyan),
        -2px 0 0 var(--dirty-white),
        var(--glow-cyan-hover);
    transform: translateX(1px);
}

.zh-button:focus-visible {
    outline: 2px solid var(--electric-cyan);
    outline-offset: 2px;
}

.zh-button:active:not(:disabled) {
    transform: translateX(1px) scaleY(0.97);
    filter: brightness(0.95);
}

.zh-button:disabled {
    cursor: not-allowed;
    filter: saturate(0.5) brightness(0.8);
    box-shadow: none;
}

.zh-button__cursor {
    animation: zh-button-cursor 1.1s steps(1) infinite;
}

@keyframes zh-button-cursor {
    0%,
    49% {
        opacity: 1;
    }
    50%,
    100% {
        opacity: 0;
    }
}

@media (prefers-reduced-motion: reduce) {
    .zh-button {
        transition: none;
    }

    .zh-button:hover:not(:disabled),
    .zh-button:active:not(:disabled) {
        transform: none;
    }

    .zh-button__cursor {
        animation: none;
    }
}
</style>
